// Browser entry — Sentry browser SDK init + RR v7 hydration.
//
// Sentry.init() runs at module-evaluation time so the SDK is ready before
// hydration. The RR v7 framework-mode integration uses
// reactRouterTracingIntegration() (NOT browserTracingIntegration() — the
// react-router variant adds RR-aware tx names) and the
// `<HydratedRouter onError={Sentry.sentryOnError} />` hook to capture errors
// thrown by route loaders/actions/render at the top of the React tree.
//
// DSN is build-time-baked via VITE_SENTRY_DSN (Vite injects import.meta.env
// at build). The init is gated on DSN presence so local-dev builds without a
// DSN no-op cleanly.
import * as Sentry from '@sentry/react-router';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'local',
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [Sentry.reactRouterTracingIntegration()],
    tracesSampleRate: 0.1,
    // Drop bot-noise that React Router's sentryOnError surfaces:
    //   - 404s from URL probes (`No route matches URL "/privecy-policy"`)
    //   - non-GET requests to actionless routes (`POST /` from scanners)
    // Both are the public marketing site catching crawler traffic, not real
    // user errors. Real route errors (loaders/actions throwing inside the
    // app) still flow through.
    beforeSend(event) {
      const ex = event.exception?.values?.[0];
      const msg = ex?.value ?? '';
      if (msg.startsWith('No route matches URL')) return null;
      if (/did not provide an `action` for route/.test(msg)) return null;
      return event;
    },
  });
}

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <HydratedRouter onError={Sentry.sentryOnError} />
    </StrictMode>,
  );
});
